import React, { useEffect, useRef, useState } from 'react';
import { addDoc, deleteDoc, limit, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { photoDoc, photosColl } from '../db';
import { UserProfile } from '../App';
import { recordActivity } from '../utils/activityFeed';

interface Props {
  user: UserProfile;
}

interface PhotoMini {
  id: string;
  url: string;
  publicId?: string;
  kind?: string;
  addedBy: string;
  addedByName: string;
  timestamp: any;
}

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dt5ndnmh6';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'lovers';
const UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;

async function uploadInstantPhoto(file: File, userId: string): Promise<{ url: string; publicId: string }> {
  const folder = `serhat-ayca/instant/${userId}`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);
  formData.append('public_id', `${folder}/${Date.now()}`);

  const res = await fetch(UPLOAD_URL, { method: 'POST', body: formData });
  if (!res.ok) throw new Error('Cloudinary yükleme hatası');
  const data = await res.json();
  return { url: data.secure_url, publicId: data.public_id };
}

export default function InstantPhotoWidget({ user }: Props) {
  const [photos, setPhotos] = useState<PhotoMini[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoMini | null>(null);
  const [draftPhoto, setDraftPhoto] = useState<File | null>(null);
  const [draftPreviewUrl, setDraftPreviewUrl] = useState('');
  const [draftLabel, setDraftLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const partner = user.displayName === 'Serhat' ? 'Ayça' : 'Serhat';

  useEffect(() => {
    const q = query(photosColl(user.coupleId), orderBy('timestamp', 'desc'), limit(80));
    return onSnapshot(q, snap => {
      setPhotos(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() } as PhotoMini & { kind?: string }))
          .filter(photo => photo.kind === 'instant')
      );
    });
  }, [user.coupleId]);

  const mine = photos.find(photo => photo.addedBy === user.uid) || null;
  const theirs = photos.find(photo => photo.addedBy !== user.uid) || null;

  const pickPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (draftPreviewUrl) URL.revokeObjectURL(draftPreviewUrl);
    const previewUrl = URL.createObjectURL(file);
    setDraftPhoto(file);
    setDraftPreviewUrl(previewUrl);
    setDraftLabel(file.name);
    setStatus('Fotoğraf seçildi. Şimdi paylaşabilirsiniz.');
    e.target.value = '';
  };

  const clearDraft = () => {
    if (draftPreviewUrl) URL.revokeObjectURL(draftPreviewUrl);
    setDraftPhoto(null);
    setDraftPreviewUrl('');
    setDraftLabel('');
  };

  const shareDraftPhoto = async () => {
    if (!draftPhoto) return;

    setUploading(true);
    setStatus('Fotoğraf paylaşılıyor...');
    try {
      const { url, publicId } = await uploadInstantPhoto(draftPhoto, user.uid);
      await addDoc(photosColl(user.coupleId), {
        url,
        publicId,
        caption: '',
        albumId: null,
        albumName: 'Günden kalan kareler',
        kind: 'instant',
        addedBy: user.uid,
        addedByName: user.displayName,
        timestamp: serverTimestamp(),
      });
      recordActivity(user, {
        type: 'instant_photo',
        title: 'Günden bir kare eklendi',
        body: `${user.displayName} bugüne bir fotoğraf bıraktı.`,
        sourceId: user.uid,
      }).catch(() => {});
      setStatus('Fotoğraf paylaşıldı.');
      clearDraft();
      setTimeout(() => setStatus(''), 2200);
    } catch {
      setStatus('Fotoğraf paylaşılamadı. Tekrar deneyin.');
      setTimeout(() => setStatus(''), 2500);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (draftPreviewUrl) URL.revokeObjectURL(draftPreviewUrl);
    };
  }, [draftPreviewUrl]);

  const removePhoto = async (photo: PhotoMini) => {
    if (!window.confirm('Bu fotoğrafı sil?')) return;
    await deleteDoc(photoDoc(user.coupleId, photo.id));
    setSelectedPhoto(null);
  };

  return (
    <section className="instant-photo-widget">
      <div className="instant-photo-head">
        <div>
          <p>Bugünden bir anını paylaş.</p>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading}>
          Fotoğraf seç
        </button>
      </div>

      {draftPhoto && (
        <div className="instant-photo-draft">
          <img src={draftPreviewUrl} alt="Seçilen fotoğraf önizlemesi" />
          <div className="instant-photo-draft-meta">
            <div>
              <strong>Seçildi</strong>
              <span>{draftLabel}</span>
            </div>
            <div className="instant-photo-draft-actions">
              <button onClick={shareDraftPhoto} disabled={uploading}>
                {uploading ? 'Paylaşılıyor...' : 'Fotoğrafı paylaş'}
              </button>
              <button onClick={clearDraft} disabled={uploading}>
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="instant-photo-pair">
        {[
          { label: user.displayName, photo: mine, mine: true },
          { label: theirs?.addedByName || partner, photo: theirs, mine: false },
        ].map(slot => (
          <button
            key={slot.label}
            className="instant-photo-slot"
            onClick={() => slot.photo ? setSelectedPhoto(slot.photo) : slot.mine ? fileRef.current?.click() : undefined}
            disabled={!slot.photo && !slot.mine}
          >
            {slot.photo ? (
              <img src={slot.photo.url} alt={`${slot.label} fotoğrafı`} />
            ) : (
              <span>{slot.mine ? '+' : '·'}</span>
            )}
            <b>{slot.label}</b>
          </button>
        ))}
      </div>

      {(status || (!mine && !theirs)) && (
        <p className="instant-photo-status">{status || 'Bugünden küçük bir an ekleyebilirsin.'}</p>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={pickPhoto}
        style={{ display: 'none' }}
      />

      {selectedPhoto && (
        <div className="modal-overlay" onClick={() => setSelectedPhoto(null)}>
          <div className="instant-photo-modal" onClick={e => e.stopPropagation()}>
            <img src={selectedPhoto.url} alt="Fotoğraf" />
            <div className="instant-photo-modal-meta">
              <p>{selectedPhoto.addedByName}</p>
              <button onClick={() => removePhoto(selectedPhoto)}>Sil</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
